import { AddAgentApiBookingRequest, AddAgentApiBookingResponse, AddAgentApiLeadRequest, AddAgentApiLeadResponse, AddAgentApiSimpleBookingRequest, AddAgentApiSimpleBookingResponse, CancelAgentApiBookingRequest, CancelAgentApiBookingResponse, CheckAgentApiBookingRequirementsRequest, CheckAgentApiBookingRequirementsResponse, CloseAgentApiListRequest, CloseAgentApiListResponse, DownloadAgentApiBookingsRequest, DownloadAgentApiBookingsResponse, DownloadAgentApiPersonalHistoryRequest, EditAgentApiBookingRequest, EditAgentApiBookingResponse, EndAgentApiTimeRecordRequest, GetAgentApiAutocompletionDetailsResponse, GetAgentApiAutocompletionRequest, GetAgentApiAutocompletionResponse, GetAgentApiBestMeetingPointRequest, GetAgentApiBestMeetingPointResponse, GetAgentApiBookingContextRequest, GetAgentApiBookingContextResponse, GetAgentApiBookingRequest, GetAgentApiBookingResponse, GetAgentApiBookingsRequest, GetAgentApiBookingsResponse, GetAgentApiEmployeeDocumentationRequest, GetAgentApiEmployeeDocumentationResponse, GetAgentApiListRequest, GetAgentApiListResponse, GetAgentApiNearestMeetingPointsRequest, GetAgentApiNearestMeetingPointsResponse, GetAgentApiNextReferenceResponse, GetAgentApiOrganizationRequest, GetAgentApiOrganizationResponse, GetAgentApiPersonalHistoryRequest, GetAgentApiPersonalHistoryResponse, GetAgentApiPickupTimeRequest, GetAgentApiPickupTimeResponse, GetAgentApiPricingRequest, GetAgentApiPricingResponse, GetAgentApiSimpleBookingContextRequest, GetAgentApiSimpleBookingContextResponse, GetAgentApiTimeRecordResponse, GetAgentApiZonesResponse, GetAllAgentApiActivitiesRequest, GetAllAgentApiActivitiesResponse, GetAllAgentApiHotelsResponse, GetAllAgentApiListsResponse, GetAllAgentApiMeetingPointsResponse, GetAvailableAgentApiDocumentationResponse, LogAgentApiInRequest, LogAgentApiInResponse, NotifyAgentApiListRequest, NotifyAgentApiListResponse, OpenAgentApiListRequest, OpenAgentApiListResponse, ResetAgentApiPasswordRequest, ResetAgentApiPasswordResponse, SendAgentApiBookingVoucherRequest, SendAgentApiBookingVoucherResponse, StartAgentApiTimeRecordRequest, StartAgentApiTimeRecordResponse, TakeAgentApiListPhotoRequest, TakeAgentApiListPhotoResponse, TransferAgentApiListBookingRequest, TransferAgentApiListBookingResponse, UpdateAgentApiListBookingRequest, UpdateAgentApiListBookingResponse, UpsellAgentApiBookingRequest, UpsellAgentApiBookingResponse } from "@/services/ClientInterfaces";
import { BaseService } from "./BaseService";
import { EventBus } from "@/components/EventBus";
import { Notify } from "q-virtual-scroll";

export interface Filter {
    from: string;
    to: string;
    creationFrom: string;
    creationTo: string;
    status: string[];
    hotel: string[];
    product: string[];
    user: string[];
}

export interface DateFilterItem {
    from: string;
    to: string;
}

export class DataClient {

    static get Agencies(): AgenciesService {
        return AgenciesService.instance;
    }

    static get Dialogs(): DialogService {
        return DialogService.instance;
    }

}

class AgenciesService extends BaseService {

    public static readonly instance = new AgenciesService();

    public async getOrganization(request: GetAgentApiOrganizationRequest): Promise<GetAgentApiOrganizationResponse> {
        return await this.httpRawPost<GetAgentApiOrganizationResponse>(["organization"], request);
    }

    public async logIn(request: LogAgentApiInRequest): Promise<LogAgentApiInResponse> {
        return await this.httpRawPost<LogAgentApiInResponse>(["login"], request);
    }

    public async resetPassword(request: ResetAgentApiPasswordRequest): Promise<ResetAgentApiPasswordResponse> {
        return await this.httpPost<ResetAgentApiPasswordResponse>(["reset-password"], request);
    }

    public async getAvailableDocumentation(): Promise<GetAvailableAgentApiDocumentationResponse> {
        return await this.httpGet<GetAvailableAgentApiDocumentationResponse>(["documentation"]);
    }

    public async getBookingContext(): Promise<GetAgentApiBookingContextResponse> {
        return await this.httpGet<GetAgentApiBookingContextResponse>(["booking-context"]);
    }

    public async addSimpleBooking(request: AddAgentApiSimpleBookingRequest): Promise<AddAgentApiSimpleBookingResponse> {
        return await this.httpPost<AddAgentApiSimpleBookingResponse>(["simple-booking"], request);
    }

    public async getSimpleBookingContext(request: GetAgentApiSimpleBookingContextRequest): Promise<GetAgentApiSimpleBookingContextResponse> {
        return await this.httpPost<GetAgentApiSimpleBookingContextResponse>(["simple-booking", "context"], request);
    }

    public async getAllHotels(): Promise<GetAllAgentApiHotelsResponse> {
        return await this.httpGet<GetAllAgentApiHotelsResponse>(["hotels"]);
    }

    public async getAllBookings(request: GetAgentApiBookingsRequest): Promise<GetAgentApiBookingsResponse> {
        return await this.httpGet<GetAgentApiBookingsResponse>(["bookings"], {
            from: request.startDate || "",
            to: request.endDate || "",
            creationFrom: request.creationStartDate || "",
            creationTo: request.creationEndDate || "",
        });
    }

    public async getBooking(request: GetAgentApiBookingRequest): Promise<GetAgentApiBookingResponse> {
        return await this.httpGet<GetAgentApiBookingResponse>(["bookings", request.id]);
    }

    public async sendBookingVoucher(request: SendAgentApiBookingVoucherRequest): Promise<SendAgentApiBookingVoucherResponse> {
        return await this.httpPost<SendAgentApiBookingVoucherResponse>(["bookings", request.id, "voucher", "send"], request);
    }

    public async editBooking(request: EditAgentApiBookingRequest): Promise<EditAgentApiBookingResponse> {
        return await this.httpPost<EditAgentApiBookingResponse>(["bookings", request.id, "edit"], request);
    }

    public async cancelBooking(request: CancelAgentApiBookingRequest): Promise<CancelAgentApiBookingResponse> {
        return await this.httpPost<CancelAgentApiBookingResponse>(["bookings", request.id, "cancel"], request);
    }

    public async addBooking(request: AddAgentApiBookingRequest): Promise<AddAgentApiBookingResponse> {
        return await this.httpPost<AddAgentApiBookingResponse>(["bookings"], request);
    }

    public async getAllActivities(request: GetAllAgentApiActivitiesRequest): Promise<GetAllAgentApiActivitiesResponse> {
        return await this.httpPost<GetAllAgentApiActivitiesResponse>(["activities"], request);
    }

    public async getAllMeetingPoints(): Promise<GetAllAgentApiMeetingPointsResponse> {
        return await this.httpGet<GetAllAgentApiMeetingPointsResponse>(["meeting-points"]);
    }

    public async getAllZones(): Promise<GetAgentApiZonesResponse> {
        return await this.httpGet<GetAgentApiZonesResponse>(["zones"]);
    }

    public async autocompleteAddress(query: string): Promise<GetAgentApiAutocompletionResponse> {
        return await this.httpGet<GetAgentApiAutocompletionResponse>(["address", "autocomplete"], { query });
    }

    public async getAutocompletionDetails(id: string): Promise<GetAgentApiAutocompletionDetailsResponse> {
        // return this.httpGet<GetAgentApiAutocompletionDetailsResponse>(["address", "autocomplete", id]);
        return this.httpPost<GetAgentApiAutocompletionDetailsResponse>(["address", "autocomplete", "by-id"], { id });
    }

    public async getBestMeetingPoint(request: GetAgentApiBestMeetingPointRequest): Promise<GetAgentApiBestMeetingPointResponse> {
        return this.httpPost<GetAgentApiBestMeetingPointResponse>(["meeting-points", "best"], request);
    }

    public async getNextReference(): Promise<GetAgentApiNextReferenceResponse> {
        return this.httpGet<GetAgentApiNextReferenceResponse>(["bookings", "next-reference"]);
    }

    public async getPickupTime(request: GetAgentApiPickupTimeRequest): Promise<GetAgentApiPickupTimeResponse> {
        return this.httpPost<GetAgentApiPickupTimeResponse>(["pickup-time"], request);
    }

    public async getNearestMeetingPoints(request: GetAgentApiNearestMeetingPointsRequest): Promise<GetAgentApiNearestMeetingPointsResponse> {
        return this.httpPost<GetAgentApiNearestMeetingPointsResponse>(["nearest-meeting-points"], request);
    }

    public async getPricing(request: GetAgentApiPricingRequest): Promise<GetAgentApiPricingResponse> {
        return this.httpPost<GetAgentApiPricingResponse>(["pricing"], request);
    }

    public async getAllLists(): Promise<GetAllAgentApiListsResponse> {
        return this.httpGet<GetAllAgentApiListsResponse>(["lists"]);
    }

    public async getList(request: GetAgentApiListRequest): Promise<GetAgentApiListResponse> {
        return this.httpGet<GetAgentApiListResponse>(["lists", request.id]);
    }

    public async updateListBooking(request: UpdateAgentApiListBookingRequest): Promise<UpdateAgentApiListBookingResponse> {
        return this.httpPost<UpdateAgentApiListBookingResponse>(["lists", request.id, "update-booking"], request);
    }

    public async openList(request: OpenAgentApiListRequest): Promise<OpenAgentApiListResponse> {
        return this.httpPost<OpenAgentApiListResponse>(["lists", request.id, "open"], request);
    }

    public async closeList(request: CloseAgentApiListRequest): Promise<CloseAgentApiListResponse> {
        return this.httpPost<CloseAgentApiListResponse>(["lists", request.id, "close"], request);
    }

    public async takeListPhoto(request: TakeAgentApiListPhotoRequest): Promise<TakeAgentApiListPhotoResponse> {
        return this.httpPost<TakeAgentApiListPhotoResponse>(["lists", request.id, "photo"], request);
    }

    public async downloadBookings(request: DownloadAgentApiBookingsRequest): Promise<void> {
        const blob = await this.httpPostAsBlob<DownloadAgentApiBookingsResponse>(["bookings", "download"], request);
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
    }

    public async viewDocumentation(id: string): Promise<void> {
        const url = this.buildTargetUrl(["documentation", id, "view"]);
        window.open(url, "_blank");
    }

    public async downloadDocumentation(id: string): Promise<void> {
        const url = this.buildTargetUrl(["documentation", id, "download"]);
        window.open(url, "_blank");
    }

    public async upsellBooking(request: UpsellAgentApiBookingRequest): Promise<UpsellAgentApiBookingResponse> {
        return await this.httpPost<UpsellAgentApiBookingResponse>(["bookings", "upsell"], request);
    }

    public async getEmployeeDocumentation(): Promise<GetAgentApiEmployeeDocumentationResponse> {
        return await this.httpGet<GetAgentApiEmployeeDocumentationResponse>(["employee-documentation"]);
    }

    public async notifyList(request: NotifyAgentApiListRequest): Promise<NotifyAgentApiListResponse> {
        return await this.httpPost<NotifyAgentApiListResponse>(["lists", request.id, "notify"], request);
    }

    public async transferListBooking(request: TransferAgentApiListBookingRequest): Promise<TransferAgentApiListBookingResponse> {
        return await this.httpPost<TransferAgentApiListBookingResponse>(["lists", request.scheduledServiceId, "transfer"], request);
    }

    public async getPersonalHistory(request: GetAgentApiPersonalHistoryRequest): Promise<GetAgentApiPersonalHistoryResponse> {
        return await this.httpPost<GetAgentApiPersonalHistoryResponse>(["personal-history"], request);
    }

    public async downloadPersonalHistory(request: DownloadAgentApiPersonalHistoryRequest): Promise<void> {
        await this.httpPostAsBlob(["personal-history", "download"], request, true);
    }

    public async getTimeRecord(): Promise<GetAgentApiTimeRecordResponse> {
        return await this.httpGet<GetAgentApiTimeRecordResponse>(["time-record"]);
    }

    public async startTimeRecord(request: StartAgentApiTimeRecordRequest): Promise<StartAgentApiTimeRecordResponse> {
        return await this.httpPost<StartAgentApiTimeRecordResponse>(["time-record", "start"], request);
    }

    public async endTimeRecord(request: EndAgentApiTimeRecordRequest): Promise<StartAgentApiTimeRecordResponse> {
        return await this.httpPost<StartAgentApiTimeRecordResponse>(["time-record", "end"], request);
    }

    public async addLead(request: AddAgentApiLeadRequest): Promise<AddAgentApiLeadResponse> {
        return await this.httpPost<AddAgentApiLeadResponse>(["leads"], request);
    }

    public async checkBookingRequirements(request: CheckAgentApiBookingRequirementsRequest): Promise<CheckAgentApiBookingRequirementsResponse> {
        return await this.httpPost<CheckAgentApiBookingRequirementsResponse>(["check-booking-requirements"], request);
    }

}



export interface ConfirmationDialogRequest {
    title: string;
    message?: string;
    messages?: string[];
    debug?: any;
    warningMessage?: string;
    questions?: string[];
    inputs?: {
        type?: "text" | "text-area" | "select"
        label: string;
        value?: string;
        items?: Array<{ id: string, name: string }>;
    }[];
    nocancel?: boolean;
    color?: string;
    width?: string;
    icon?: string;
    iconColor?: string;
    buttonColor?: string;
    acceptButtonText?: string;
    cancelButtonText?: string;
    fullScreen?: boolean;
    bigButtons?: boolean;
    type?: "normal" | "warning" | "error";
    iconPosition?: "top" | "left";
}

export interface ToastRequest {
    title: string;

    message?: string;
    color?: string;

    buttonText?: string;
    buttonColor?: string;
    buttonIcon?: string;
    buttonRoute?: any;
}

class DialogService {

    public static readonly instance = new DialogService();

    public readonly OpenToastEvent = "toast:open";

    public readonly OpenConfirmationDialogEvent = "dialog:confirmation:open";
    public readonly CloseConfirmationDialogEvent = "dialog:confirmation:close";

    public readonly OpenErrorDialogEvent = "dialog:error:open";
    public readonly CloseErrorDialogEvent = "dialog:error:close";

    public toast(request: ToastRequest) {
        EventBus.$emit(this.OpenToastEvent, request);
    }

    public confirmation(request: ConfirmationDialogRequest | boolean, close = false): Promise<boolean> {
        if (close) {
            EventBus.$emit(this.CloseConfirmationDialogEvent, request);
            return Promise.resolve(false);
        } else {
            EventBus.$emit(this.OpenConfirmationDialogEvent, request);

            return new Promise((resolve) => {
                EventBus.$on(this.CloseConfirmationDialogEvent, (result: any) => {
                    resolve(result);
                });
            })
        }
    }

    public confirmationWithQuestions(request: ConfirmationDialogRequest | boolean[], close = false): Promise<boolean[]> {
        if (close) {
            EventBus.$emit(this.CloseConfirmationDialogEvent, request);
            return Promise.resolve([false]);
        } else {
            EventBus.$emit(this.OpenConfirmationDialogEvent, request);

            return new Promise((resolve) => {
                EventBus.$on(this.CloseConfirmationDialogEvent, (result: any) => {
                    resolve(result);
                });
            })
        }
    }

    public confirmationWithInputs(request: ConfirmationDialogRequest | [result: boolean, ...inputs: string[]], close = false): Promise<[result: boolean, ...inputs: string[]]> {
        if (close) {
            EventBus.$emit(this.CloseConfirmationDialogEvent, request);
            return Promise.resolve([false]);
        } else {
            EventBus.$emit(this.OpenConfirmationDialogEvent, request);

            return new Promise((resolve) => {
                EventBus.$on(this.CloseConfirmationDialogEvent, (result: any) => {
                    resolve(result);
                });
            })
        }
    }

    public confirmationWithQuestionsAndInputs(request: ConfirmationDialogRequest | [result: boolean, questions: boolean[], inputs: string[]], close = false): Promise<[result: boolean, questions: boolean[], inputs: string[]]> {
        if (close) {
            EventBus.$emit(this.CloseConfirmationDialogEvent, request);
            return Promise.resolve([false, [], []]);
        } else {
            EventBus.$emit(this.OpenConfirmationDialogEvent, request);

            return new Promise((resolve) => {
                EventBus.$on(this.CloseConfirmationDialogEvent, (result: any) => {
                    resolve(result);
                });
            })
        }
    }

}
